import { Controller } from '@hotwired/stimulus'
import type { Blob } from '@rails/activestorage'
import { DirectUpload } from '@rails/activestorage'
import { FileChecksum } from '@rails/activestorage/src/file_checksum'
import { destroy, get, post } from '@rails/request.js'
import AwsS3 from '@uppy/aws-s3'
import Uppy from '@uppy/core'
import { formatBytes } from '../utils'
import ProgressBarController from './progress-bar'

type SimpleFile = Pick<File, 'name' | 'size'>

export interface AttachEvent extends Event {
  detail: {
    blob?: Blob
    file: File
  }
}

export interface BusyChangeEvent extends Event {
  detail: { busy: boolean }
}

export interface DirectUploadProgressEvent extends Event {
  loaded: number
  total: number
}

type DetachType = 'delete' | 'purge' | 'soft-delete'

export default class AttachmentManagerItemController extends Controller<HTMLElement> {
  declare attachInputNameValue: string
  declare blobValue: Blob
  declare busyValue: boolean
  declare detachedTypeValue: DetachType
  declare detachedValue: boolean
  declare detachInputNameValue: string
  declare fileValue: SimpleFile
  declare persistedValue: boolean
  declare progressValue: number
  declare securedValue: boolean
  declare readonly attachInputTarget: HTMLInputElement
  declare readonly attachmentMetadataSignedIdTarget: HTMLInputElement
  declare readonly bulkCheckboxTarget: HTMLInputElement
  declare readonly detachInputTarget: HTMLInputElement
  declare readonly fileNameTarget: HTMLAnchorElement
  declare readonly hasBulkCheckboxTarget: boolean
  declare readonly hasProgressBarOutlet: boolean
  declare readonly fileSizeTarget: HTMLElement
  declare readonly hasAttachmentMetadataSignedIdTarget: boolean
  declare readonly progressBarOutlet: ProgressBarController
  declare readonly unpersistedIndicatorTarget: HTMLSpanElement

  // instance variables that live outside of Stimulus because
  // Stimulus doesn't have a way to store rich data
  declare file?: File
  declare request?: XMLHttpRequest

  static outlets = ['progress-bar']

  static targets = [
    'attachInput',
    'attachmentMetadataSignedId',
    'bulkCheckbox',
    'detachInput',
    'fileName',
    'fileSize',
    'progressBar',
    'unpersistedIndicator',
  ]

  static values = {
    attachInputName: String,
    blob: Object,
    busy: Boolean,
    detached: Boolean,
    detachedType: { type: String, default: 'delete' },
    detachInputName: String,
    file: { type: Object, default: { name: 'No file selected', size: 0 } },
    persisted: { type: Boolean, default: true },

    // -2: inactive
    // -1: processing
    // 0-100: progress percentage
    progress: { type: Number, default: -2 },
    secured: Boolean,
  }

  BLOB_PATH = '/rails/active_storage/blobs'
  UPLOAD_PATH = '/rails/active_storage/direct_uploads'
  FILE_MULTIPART_THRESHOLD = 2 ** 30 * 0.1 // in GBs

  uppy: Uppy | undefined

  get headers() {
    return {
      'X-Secured-Attachment': this.securedValue.toString(),
    }
  }

  connect() {
    this.#handleBusyChange()
    this.#handleFileChange()
    this.#handlePersistedChange()
    this.#handleProgressChange()
  }

  // ActiveStorage DirectUpload handlers

  directUploadWillStoreFileWithXHR(request: XMLHttpRequest) {
    this.request = request
    this.request.upload.addEventListener('progress', this.#setProgress.bind(this))
  }

  // Public methods

  attach(file: File) {
    this.dispatch('beforeAttach', { detail: { file } } satisfies Partial<AttachEvent>)
    this.file = file
    this.fileValue = { name: file.name, size: file.size }
    this.persistedValue = false
    this.progressValue = -1 // processing
    this.#toggleFileNameInteractive(false)

    if (file.size < this.FILE_MULTIPART_THRESHOLD) {
      new DirectUpload(file, this.UPLOAD_PATH, this, this.headers).create((error, blob) => {
        if (error) return this.#handleError(error)
        if (!blob) return this.#handleError(new Error('Upload succeeded but there was an internal data issue.'))

        this.blobValue = blob
        this.#toggleFileNameInteractive(true)
        this.#dispatchInputEvent()
      })
    } else {
      this.#multipartUpload()
    }
  }

  cancelMultipartUpload() {
    this.uppy?.cancelAll()
    this.#resetProgressValues()
  }

  detach() {
    this.detachedValue = true
    this.#dispatchInputEvent()
  }

  reattach() {
    this.detachedValue = false
  }

  getBlobUrl(download = false) {
    const url = new URL(
      `${window.location.origin}/rails/active_storage/blobs/${this.blobValue.signed_id}/${this.blobValue.filename}`,
    )
    if (download) {
      url.searchParams.append('disposition', 'attachment')
    }
    return url.href
  }

  // Value change callbacks
  blobValueChanged = this.#handleBlobChange.bind(this)
  busyValueChanged = this.#handleBusyChange.bind(this)
  detachedTypeValueChanged = this.#handleDetachedTypeValueChange.bind(this)
  detachedValueChanged = this.#handleDetachedChange.bind(this)
  fileValueChanged = this.#handleFileChange.bind(this)
  persistedValueChanged = this.#handlePersistedChange.bind(this)
  progressValueChanged = this.#handleProgressChange.bind(this)

  // Private methods

  #calculateChecksum(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      FileChecksum.create(file, (error: Error, checksum: string) => {
        if (error) {
          return reject(error)
        }
        resolve(checksum)
      })
    })
  }

  #detach() {
    switch (this.detachedTypeValue) {
      case 'purge':
        this.#detachViaPurge()
        break

      case 'soft-delete':
        this.#detachViaSoftDelete()
        break

      case 'delete':
        this.#detachViaDelete()
        break
    }

    this.dispatch('detach')
  }

  #detachViaPurge() {
    this.element.classList.add('hidden')
    this.attachInputTarget.disabled = true
    this.detachInputTarget.disabled = false
    this.dispatch('purge')
  }

  #detachViaSoftDelete() {
    this.element.classList.add('detached', 'line-through', 'text-gray-400')
    this.detachInputTarget.disabled = false
    this.dispatch('soft-delete')
  }

  #detachViaDelete() {
    this.request?.abort()
    this.uppy?.cancelAll()
    this.#resetProgressValues()
    this.element.remove()
    this.dispatch('delete')
  }

  #dispatchInputEvent() {
    // NOTE: This tells FormChangeController that a modification was made
    this.dispatch('input', { prefix: '' })
  }

  #handleBlobChange() {
    const { filename, signed_id } = this.blobValue

    if (!filename || !signed_id) return

    this.fileNameTarget.href ||= this.getBlobUrl()
    this.attachInputTarget.disabled = signed_id.length === 0
    this.attachInputTarget.value = signed_id

    if (this.hasBulkCheckboxTarget) {
      this.bulkCheckboxTarget.disabled = false
      this.bulkCheckboxTarget.value = signed_id
    }

    if (this.hasAttachmentMetadataSignedIdTarget) {
      this.attachmentMetadataSignedIdTarget.value = signed_id
    }

    this.dispatch('attach', { detail: { blob: this.blobValue, file: this.file } })
  }

  #handleBusyChange() {
    this.element.classList.toggle('busy', this.busyValue)
    this.dispatch('busyChange', { detail: { busy: this.busyValue } } satisfies Partial<BusyChangeEvent>)
  }

  #handleDetachedChange() {
    this.detachedValue ? this.#detach() : this.#reattach()
  }

  #handleDetachedTypeValueChange() {
    this.persistedValue = this.detachedTypeValue !== 'delete'
  }

  #handleError(error: Error) {
    this.dispatch('error')
    this.#resetProgressValues()
    this.element.remove()
    alert(`Upload failed: ${error}`)
  }

  #handleFileChange() {
    this.#setUnpersistedIndicatorVisibility()
    this.#updateFileName()
    this.#updateFileSize()
  }

  #handlePersistedChange() {
    this.unpersistedIndicatorTarget.classList.toggle('hidden', this.persistedValue)

    if (this.persistedValue === false && this.hasProgressBarOutlet) {
      this.progressBarOutlet.element.classList.remove('hidden')
    }
  }

  #handleProgressChange() {
    const inactive = this.progressValue === -2
    const started = this.progressValue === 0
    const done = this.progressValue === 100

    if (inactive) return

    this.busyValue = !inactive && !done

    if (this.hasProgressBarOutlet) {
      this.progressBarOutlet.progressValue = this.progressValue
    }

    if (started) {
      this.dispatch('uploadStarted')
    } else if (done) {
      this.dispatch('uploadFinished')
    }
  }

  async #multipartUpload(): Promise<void> {
    this.uppy = new Uppy()

    this.uppy.use(AwsS3, {
      shouldUseMultipart: _file => {
        return true
      },
      getUploadParameters: async () => {
        return { url: '' } // Should never be called bc shouldUseMultipart is always true
      },
      createMultipartUpload: async uppyFile => {
        const uppyFileChecksum = await this.#calculateChecksum(uppyFile.data as File)

        const response = await post('/multipart_uploads', {
          body: {
            blob: {
              byte_size: uppyFile.size,
              checksum: uppyFileChecksum,
              content_type: uppyFile.type,
              filename: uppyFile.name,
            },
          },
          headers: this.headers,
        })

        const data = await response.json

        this.uppy?.setFileMeta(uppyFile.id, { blobSignedId: data.blob_signed_id })

        return {
          uploadId: data.upload_id,
          key: data.blob_key,
        }
      },
      signPart: async (uppyFile, { uploadId, partNumber }) => {
        const response = await get('/multipart_uploads/sign_part', {
          query: {
            blob_signed_id: uppyFile.meta.blobSignedId,
            part_number: partNumber,
            upload_id: uploadId,
          },
        })

        return await response.json
      },
      listParts: async (uppyFile, { uploadId }) => {
        const response = await get('/multipart_uploads/list_parts', {
          query: {
            blob_signed_id: uppyFile.meta.blobSignedId,
            upload_id: uploadId,
          },
        })

        const data = await response.json
        return data.parts
      },
      completeMultipartUpload: async (uppyFile, { uploadId, parts }) => {
        const response = await post('/multipart_uploads/complete', {
          body: {
            blob_signed_id: uppyFile.meta.blobSignedId,
            parts: parts,
            upload_id: uploadId,
          },
        })

        return await response.json
      },
      abortMultipartUpload: async (_uppyFile, { uploadId, key }) => {
        await destroy('/multipart_uploads/abort', {
          query: {
            blob_key: key,
            upload_id: uploadId,
          },
        })
      },
    })

    this.uppy.on('error', error => {
      this.#handleError(error)
    })

    this.uppy.on('upload-progress', (_file, progress) => {
      if (progress.bytesTotal == null) return
      this.progressValue = Math.round((progress.bytesUploaded / progress.bytesTotal) * 100)
    })

    this.uppy.on('complete', result => {
      if (!result.successful?.length) return

      const blob = result.successful[0].response?.body

      if (!blob) return this.#handleError(new Error('File upload completed but the response was missing.'))

      this.blobValue = blob as unknown as Blob
      this.#toggleFileNameInteractive(true)
      this.#dispatchInputEvent()
    })

    if (!this.file) return

    this.uppy.addFile({
      name: this.file.name,
      type: this.file.type,
      data: this.file,
    })

    this.uppy.upload()
  }

  #reattach() {
    // NOTE: Only soft-deleted attachments can be reattached
    if (this.detachedTypeValue !== 'soft-delete') return

    this.element.classList.remove('detached', 'line-through', 'text-gray-400')
    this.detachInputTarget.disabled = true
    this.dispatch('reattach')
  }

  #resetProgressValues() {
    this.busyValue = false
    this.progressValue = -2
  }

  #setProgress(event: DirectUploadProgressEvent) {
    this.progressValue = Math.round((event.loaded / event.total) * 100)
  }

  #setUnpersistedIndicatorVisibility() {
    this.unpersistedIndicatorTarget.classList.toggle('hidden', this.fileValue.size === 0)
  }

  #toggleFileNameInteractive(clickable: boolean) {
    const busyClasses = ['cursor-wait', 'text-gray-400']

    if (clickable) {
      this.fileNameTarget.classList.remove(...busyClasses)
    } else {
      this.fileNameTarget.classList.add(...busyClasses)
    }
  }

  #updateFileName() {
    const fileNameSpan = this.fileNameTarget.firstChild as HTMLSpanElement
    const { name } = this.fileValue

    fileNameSpan.innerHTML = name
    this.fileNameTarget.dataset.tooltipText = name
  }

  #updateFileSize() {
    const { size } = this.fileValue

    this.fileSizeTarget.classList.toggle('hidden', size === 0)
    this.fileSizeTarget.innerText = formatBytes(size)
  }
}
